import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
} from "@mui/material";
import { motion } from "framer-motion";
import {
  Search,
  ChevronDown,
  Copy,
  Check,
  Lock,
  Globe,
  Shield,
  Bot,
  MessageCircle,
  BrainCircuit,
  BookOpen,
  BarChart3,
  Headphones,
  Users,
  Link,
  Zap,
  ShoppingCart,
  ClipboardList,
  Upload,
  Key,
  Cable,
  Wifi,
} from "lucide-react";
import BRAND from "../theme/talktokam";

const methodColor = (method) => {
  switch (method) {
    case "GET":
      return "#10b981";
    case "POST":
      return "#6366f1";
    case "PUT":
      return "#f59e0b";
    case "PATCH":
      return "#8b5cf6";
    case "DELETE":
      return "#ef4444";
    default:
      return "#94a3b8";
  }
};

const TOC_SECTIONS = [
  { id: "auth", icon: Lock, title: "Authentication" },
  { id: "bots", icon: Bot, title: "Bots" },
  { id: "conversations", icon: MessageCircle, title: "Conversations" },
  { id: "ai-agents", icon: BrainCircuit, title: "AI Agents" },
  { id: "knowledge", icon: BookOpen, title: "Knowledge Base" },
  { id: "crawl", icon: Globe, title: "Website Crawl" },
  { id: "analytics", icon: BarChart3, title: "Analytics" },
  { id: "live-chat", icon: Headphones, title: "Live Chat" },
  { id: "agents", icon: Users, title: "Agent Management" },
  { id: "webhooks", icon: Link, title: "Webhooks" },
  { id: "canned", icon: Zap, title: "Canned Responses" },
  { id: "orders", icon: ShoppingCart, title: "Orders" },
  { id: "plans", icon: ClipboardList, title: "Plans & Billing" },
  { id: "upload", icon: Upload, title: "File Upload" },
  { id: "2fa", icon: Key, title: "Two-Factor Auth" },
  { id: "admin", icon: Shield, title: "Admin" },
  { id: "proxy", icon: Cable, title: "Proxy" },
];

const SectionCard = ({ id, icon: Icon, title, children }) => (
  <Box id={id} sx={{ scrollMarginTop: 100 }}>
    <Box
      sx={{
        p: { xs: 2.5, md: 4 },
        borderRadius: 4,
        bgcolor: "rgba(255,255,255,0.04)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.06)",
        mb: 3,
        transition: "all 0.3s",
        "&:hover": {
          borderColor: `${BRAND.cyan}33`,
          boxShadow: `0 8px 40px ${BRAND.cyan}08`,
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: `${BRAND.cyan}18`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Icon size={20} color={BRAND.cyan} />
        </Box>
        <Typography
          variant="h3"
          sx={{ fontWeight: 800, fontSize: "1.3rem", color: "#fff" }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  </Box>
);

const AuthBadge = ({ type }) => {
  if (type === "none")
    return (
      <Chip
        icon={<Globe size={12} />}
        label="Public"
        size="small"
        sx={{
          bgcolor: "#10b98118",
          color: "#10b981",
          fontSize: "0.6rem",
          height: 22,
          mr: 0.5,
        }}
      />
    );
  if (type === "jwt")
    return (
      <Chip
        icon={<Lock size={12} />}
        label="JWT"
        size="small"
        sx={{
          bgcolor: "#6366f118",
          color: "#6366f1",
          fontSize: "0.6rem",
          height: 22,
          mr: 0.5,
        }}
      />
    );
  if (type === "admin")
    return (
      <Chip
        icon={<Shield size={12} />}
        label="Admin"
        size="small"
        sx={{
          bgcolor: "#ef444418",
          color: "#ef4444",
          fontSize: "0.6rem",
          height: 22,
          mr: 0.5,
        }}
      />
    );
  return null;
};

const SchemaTable = ({ rows, title }) => (
  <Box sx={{ mb: 2 }}>
    {title && (
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 700,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.5,
          mb: 1,
        }}
      >
        {title}
      </Typography>
    )}
    <Box
      sx={{
        overflowX: "auto",
        borderRadius: 2,
        border: "1px solid rgba(255,255,255,0.06)",
      }}
    >
      <Box sx={{ minWidth: 500 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "100px 80px 1fr 60px",
            bgcolor: "rgba(255,255,255,0.03)",
            px: 2,
            py: 0.8,
          }}
        >
          {["Field", "Type", "Description", "Required"].map((h, i) => (
            <Typography
              key={i}
              sx={{
                fontSize: "0.65rem",
                fontWeight: 700,
                color: BRAND.cyan,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {h}
            </Typography>
          ))}
        </Box>
        {rows.map((r, i) => (
          <Box
            key={i}
            sx={{
              display: "grid",
              gridTemplateColumns: "100px 80px 1fr 60px",
              px: 2,
              py: 0.6,
              borderTop: "1px solid rgba(255,255,255,0.04)",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#e2e8f0",
                fontWeight: 600,
                fontFamily: "monospace",
              }}
            >
              {r.field}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.7rem",
                color: "#38bdf8",
                fontFamily: "monospace",
              }}
            >
              {r.type}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
              {r.desc}
            </Typography>
            <Typography
              sx={{ fontSize: "0.7rem", color: r.req ? "#10b981" : "#64748b" }}
            >
              {r.req ? "Yes" : "No"}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  </Box>
);

const CodeBlock = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography
          sx={{
            fontSize: "0.75rem",
            fontWeight: 700,
            color: "#64748b",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            mb: 0.5,
          }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          position: "relative",
          p: 2,
          borderRadius: 2,
          bgcolor: "#0a0e27",
          border: "1px solid rgba(255,255,255,0.06)",
          fontFamily: "monospace",
          fontSize: "0.75rem",
          color: "#cbd5e1",
          overflowX: "auto",
          lineHeight: 1.7,
          whiteSpace: "pre-wrap",
        }}
      >
        <Tooltip title={copied ? "Copied!" : "Copy"}>
          <Box
            component="button"
            onClick={handleCopy}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              bgcolor: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: 1,
              p: 0.5,
              cursor: "pointer",
              color: "#64748b",
              "&:hover": { color: "#fff" },
            }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </Box>
        </Tooltip>
        {code}
      </Box>
    </Box>
  );
};

const EndpointAccordion = ({ ep }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        bgcolor: "rgba(255,255,255,0.02)",
        borderRadius: "12px !important",
        border: "1px solid rgba(255,255,255,0.06)",
        mb: 1.5,
        "&:before": { display: "none" },
        "&.Mui-expanded": { borderColor: `${methodColor(ep.method)}33` },
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown size={18} color="#94a3b8" />}
        sx={{ "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
            width: "100%",
          }}
        >
          <Box
            sx={{
              px: 1.2,
              py: 0.2,
              borderRadius: 0.8,
              fontSize: "0.65rem",
              fontWeight: 700,
              textTransform: "uppercase",
              color: "#fff",
              bgcolor: methodColor(ep.method),
              flexShrink: 0,
            }}
          >
            {ep.method}
          </Box>
          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#e2e8f0",
              flex: 1,
            }}
          >
            {ep.path}
          </Typography>
          <AuthBadge type={ep.auth} />
          {ep.plan && (
            <Chip
              label={ep.plan}
              size="small"
              sx={{
                bgcolor: "#f59e0b18",
                color: "#f59e0b",
                fontSize: "0.55rem",
                height: 20,
              }}
            />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ pb: 2, pt: 0 }}>
        <Typography
          sx={{ fontSize: "0.85rem", color: "#94a3b8", mb: 2, lineHeight: 1.6 }}
        >
          {ep.desc}
        </Typography>
        {ep.body && <SchemaTable rows={ep.body} title="Request Body" />}
        {ep.response && <SchemaTable rows={ep.response} title="Response" />}
        {ep.error && <SchemaTable rows={ep.error} title="Error Codes" />}
        {ep.curl && <CodeBlock code={ep.curl} label="cURL Example" />}
      </AccordionDetails>
    </Accordion>
  );
};

const ENDPOINTS = {
  auth: [
    {
      method: "POST",
      path: "/api/auth/register",
      auth: "none",
      desc: "Register a new user account. Validates password strength (min 8 chars, uppercase, lowercase, digit, special char). Sends verification email.",
      body: [
        {
          field: "email",
          type: "string",
          desc: "User email address",
          req: true,
        },
        {
          field: "password",
          type: "string",
          desc: "Strong password (8+ chars, upper+lower+digit+special)",
          req: true,
        },
        {
          field: "fullName",
          type: "string",
          desc: "User's full name",
          req: true,
        },
        {
          field: "phone",
          type: "string",
          desc: "Phone with country code e.g. +919876543210",
          req: true,
        },
        {
          field: "website",
          type: "string",
          desc: "Business website URL",
          req: true,
        },
        {
          field: "termsAccepted",
          type: "boolean",
          desc: "Must be true",
          req: true,
        },
      ],
      response: [
        { field: "token", type: "string", desc: "JWT access token", req: true },
        {
          field: "verificationSent",
          type: "boolean",
          desc: "Verification email sent",
          req: true,
        },
        {
          field: "user",
          type: "object",
          desc: "{ _id, email, fullName, role }",
          req: true,
        },
      ],
      error: [
        {
          field: "400",
          type: "",
          desc: "Validation error — missing or invalid fields",
          req: false,
        },
        {
          field: "409",
          type: "",
          desc: "Email already registered",
          req: false,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/auth/register" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"email": "user@example.com", "password": "StrongP@ss1", "fullName": "John Doe", "phone": "+919876543210", "website": "https://example.com", "termsAccepted": true}\'',
    },
    {
      method: "POST",
      path: "/api/auth/login",
      auth: "none",
      desc: "Login with email and password. Returns JWT token. If 2FA is enabled, requires totpToken and returns requires2FA flag first.",
      body: [
        { field: "email", type: "string", desc: "Registered email", req: true },
        {
          field: "password",
          type: "string",
          desc: "Account password",
          req: true,
        },
        {
          field: "totpToken",
          type: "string",
          desc: "6-digit 2FA code (only if 2FA enabled)",
          req: false,
        },
      ],
      response: [
        {
          field: "token",
          type: "string",
          desc: "JWT token for authenticated requests",
          req: true,
        },
        {
          field: "user",
          type: "object",
          desc: "{ _id, email, fullName, role, plan, planLimits, ... }",
          req: true,
        },
      ],
      error: [
        { field: "401", type: "", desc: "Invalid credentials", req: false },
        {
          field: "403",
          type: "",
          desc: "Email not verified or 2FA required",
          req: false,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/auth/login" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"email": "user@example.com", "password": "StrongP@ss1"}\'',
    },
    {
      method: "GET",
      path: "/api/auth/profile",
      auth: "jwt",
      desc: "Simple profile check. Returns authenticated user's ID.",
      response: [
        {
          field: "message",
          type: "string",
          desc: "Confirmation message",
          req: true,
        },
        {
          field: "userId",
          type: "string",
          desc: "Authenticated user's ID",
          req: true,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/auth/profile" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/auth/user/:id",
      auth: "jwt",
      desc: "Get user data by ID. Users can only access their own data unless admin. Returns full user object excluding sensitive fields.",
      curl: 'curl -X GET "https://api.talktokam.app/api/auth/user/USER_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "PUT",
      path: "/api/auth/user/:id/layout-settings",
      auth: "jwt",
      desc: "Update layout/bot settings and profile fields. Supports partial updates — only send fields you want to change.",
      body: [
        {
          field: "botSettings",
          type: "object",
          desc: "Widget configuration object",
          req: false,
        },
        {
          field: "flowSetupSetting",
          type: "object",
          desc: "{ question: { list: [...] } }",
          req: false,
        },
        {
          field: "email",
          type: "string",
          desc: "New email address",
          req: false,
        },
        { field: "fullName", type: "string", desc: "Updated name", req: false },
        { field: "website", type: "string", desc: "Website URL", req: false },
        { field: "phone", type: "string", desc: "Phone number", req: false },
        { field: "gst", type: "string", desc: "GSTIN for billing", req: false },
      ],
      response: [
        {
          field: "message",
          type: "string",
          desc: "Success message",
          req: true,
        },
        {
          field: "user",
          type: "object",
          desc: "Updated user object",
          req: true,
        },
      ],
      curl: 'curl -X PUT "https://api.talktokam.app/api/auth/user/USER_ID/layout-settings" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"fullName": "John Updated", "website": "https://new-example.com"}\'',
    },
    {
      method: "PUT",
      path: "/api/auth/change-password",
      auth: "jwt",
      desc: "Change password for the authenticated user. Old password must be correct.",
      body: [
        {
          field: "oldPassword",
          type: "string",
          desc: "Current password",
          req: true,
        },
        {
          field: "newPassword",
          type: "string",
          desc: "New password (must meet strength rules)",
          req: true,
        },
      ],
      response: [
        {
          field: "message",
          type: "string",
          desc: "Success confirmation",
          req: true,
        },
      ],
      curl: 'curl -X PUT "https://api.talktokam.app/api/auth/change-password" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"oldPassword": "OldP@ss1", "newPassword": "NewP@ss2"}\'',
    },
    {
      method: "DELETE",
      path: "/api/auth/delete-account",
      auth: "jwt",
      desc: "Permanently delete your account and all associated data (bots, conversations, sub-agents). This action cannot be undone.",
      response: [
        {
          field: "message",
          type: "string",
          desc: "Deletion confirmation",
          req: true,
        },
      ],
      curl: 'curl -X DELETE "https://api.talktokam.app/api/auth/delete-account" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/auth/verify-email-token",
      auth: "none",
      desc: "Verify email address using the token sent via email.",
      body: [
        {
          field: "token",
          type: "string",
          desc: "Verification token from email",
          req: true,
        },
      ],
      response: [
        {
          field: "message",
          type: "string",
          desc: "Verification success",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/auth/verify-email-token" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"token": "VERIFICATION_TOKEN"}\'',
    },
    {
      method: "POST",
      path: "/api/auth/resend-verification",
      auth: "none",
      desc: "Resend the email verification email. Useful if the original email was lost or expired.",
      body: [
        {
          field: "email",
          type: "string",
          desc: "Registered email address",
          req: true,
        },
      ],
      response: [
        {
          field: "verificationSent",
          type: "boolean",
          desc: "Always true on success",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/auth/resend-verification" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"email": "user@example.com"}\'',
    },
    {
      method: "POST",
      path: "/api/auth/settings/whitelist",
      auth: "jwt",
      desc: "Save whitelisted domains for the chatbot widget. The widget will only appear on these domains.",
      body: [
        {
          field: "domains",
          type: "string|array",
          desc: "Comma-separated string or array of domain names",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "whitelist",
          type: "array",
          desc: "Array of whitelisted domains",
          req: true,
        },
        {
          field: "count",
          type: "number",
          desc: "Number of domains whitelisted",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/auth/settings/whitelist" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"domains": ["example.com", "app.example.com"]}\'',
    },
    {
      method: "GET",
      path: "/api/auth/install/snippet",
      auth: "jwt",
      desc: "Get the JavaScript widget embed snippet. Requires at least one whitelisted domain.",
      response: [
        {
          field: "hasWhitelist",
          type: "boolean",
          desc: "Whether whitelist is configured",
          req: true,
        },
        {
          field: "snippet",
          type: "string",
          desc: "HTML script tag for widget embed",
          req: false,
        },
        {
          field: "whitelist",
          type: "array",
          desc: "Configured domains",
          req: false,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/auth/install/snippet" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  bots: [
    {
      method: "POST",
      path: "/api/bots",
      auth: "jwt",
      desc: "Create a new chatbot. Agents cannot create bots. Respects plan limits (max bots per plan).",
      body: [
        {
          field: "name",
          type: "string",
          desc: "Bot display name (default: 'My Chatbot')",
          req: false,
        },
        {
          field: "userId",
          type: "string",
          desc: "Create bot for another user (admin only)",
          req: false,
        },
      ],
      response: [
        {
          field: "message",
          type: "string",
          desc: "Success message",
          req: true,
        },
        { field: "bot", type: "object", desc: "Created bot object", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/bots" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"name": "Customer Support Bot"}\'',
    },
    {
      method: "GET",
      path: "/api/bots",
      auth: "jwt",
      desc: "List all chatbots for the authenticated user. Admins see all bots across all users.",
      response: [
        {
          field: "—",
          type: "array",
          desc: "Array of bot objects with settings, flow, and timestamps",
          req: true,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/bots" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/bots/templates",
      auth: "jwt",
      desc: "List available industry templates that can be applied to bots.",
      response: [
        {
          field: "templates",
          type: "array",
          desc: "[{ key, name, industry, description, questionCount, typeCoverage }]",
          req: true,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/templates" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/bots/:id",
      auth: "jwt",
      desc: "Get a single bot by ID. Ownership check applies — users can only access their own bots.",
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/BOT_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "PUT",
      path: "/api/bots/:id",
      auth: "jwt",
      desc: "Update bot properties. Supports partial updates. Ownership check applies.",
      body: [
        { field: "name", type: "string", desc: "New bot name", req: false },
        {
          field: "status",
          type: "string",
          desc: "active | inactive",
          req: false,
        },
        {
          field: "botSettings",
          type: "object",
          desc: "Widget settings (theme, triggers, language)",
          req: false,
        },
        {
          field: "flowSetupSetting",
          type: "object",
          desc: "Flow configuration { question: { list: [...] } }",
          req: false,
        },
        {
          field: "ai",
          type: "object",
          desc: "AI settings (enabled, model, systemPrompt)",
          req: false,
        },
        {
          field: "autoTrigger",
          type: "object",
          desc: "Auto-trigger config (desktopDelay, mobileDelay, exitIntent)",
          req: false,
        },
      ],
      response: [
        {
          field: "message",
          type: "string",
          desc: "Success message",
          req: true,
        },
        { field: "bot", type: "object", desc: "Updated bot object", req: true },
      ],
      curl: 'curl -X PUT "https://api.talktokam.app/api/bots/BOT_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"name": "Updated Bot Name", "status": "active"}\'',
    },
    {
      method: "DELETE",
      path: "/api/bots/:id",
      auth: "jwt",
      desc: "Delete a bot and all its associated data (conversations, knowledge, etc.). Irreversible.",
      curl: 'curl -X DELETE "https://api.talktokam.app/api/bots/BOT_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/bots/:id/apply-template",
      auth: "jwt",
      desc: "Apply a predefined industry template to the bot's conversation flow.",
      body: [
        {
          field: "templateKey",
          type: "string",
          desc: "Template key from /api/bots/templates",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "template",
          type: "object",
          desc: "Applied template",
          req: true,
        },
        {
          field: "questionCount",
          type: "number",
          desc: "Number of questions added",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/bots/BOT_ID/apply-template" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"templateKey": "real-estate"}\'',
    },
    {
      method: "GET",
      path: "/api/bots/widget/:token/settings",
      auth: "none",
      desc: "Public endpoint. Get widget settings by widget token. Used by the embedded chatbot widget on your website.",
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/widget/WIDGET_TOKEN/settings"',
    },
    {
      method: "GET",
      path: "/api/bots/widget/:token/flow",
      auth: "none",
      desc: "Public endpoint. Get the conversation flow by widget token. Used by the embedded widget to render questions.",
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/widget/WIDGET_TOKEN/flow"',
    },
    {
      method: "POST",
      path: "/api/bots/:id/whitelist",
      auth: "jwt",
      desc: "Save bot-level domain whitelist. Rotates the widget token for security when domains change.",
      body: [
        {
          field: "domains",
          type: "string|array",
          desc: "Domain name(s) to whitelist",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "whitelist",
          type: "array",
          desc: "Saved whitelist",
          req: true,
        },
        {
          field: "widgetToken",
          type: "string",
          desc: "New widget token (rotated)",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/bots/BOT_ID/whitelist" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"domains": ["mysite.com"]}\'',
    },
    {
      method: "POST",
      path: "/api/bots/:id/questions/generate",
      auth: "jwt",
      desc: "AI-generate chat flow questions. Uses OpenAI or Groq depending on bot configuration.",
      body: [
        {
          field: "prompt",
          type: "string",
          desc: "Topic/context for question generation",
          req: false,
        },
        {
          field: "count",
          type: "number",
          desc: "Number of questions (1-12, default 5)",
          req: false,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "questions",
          type: "array",
          desc: "[{ type, text, options, required }]",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/bots/BOT_ID/questions/generate" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"prompt": "Real estate lead qualification", "count": 5}\'',
    },
  ],
  conversations: [
    {
      method: "POST",
      path: "/api/conversation/init",
      auth: "none",
      desc: "Public endpoint. Initialize a new conversation session. Creates or updates an existing conversation.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Unique session identifier",
          req: true,
        },
        {
          field: "botId",
          type: "string",
          desc: "Bot ID (optional)",
          req: false,
        },
        {
          field: "flow",
          type: "array",
          desc: "Flow configuration array",
          req: false,
        },
        {
          field: "userName",
          type: "string",
          desc: "Visitor's name",
          req: false,
        },
        {
          field: "intent",
          type: "string",
          desc: "Conversation intent",
          req: false,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Always true", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/conversation/init" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "UNIQUE_SESSION_ID"}\'',
    },
    {
      method: "POST",
      path: "/api/conversation/message",
      auth: "none",
      desc: "Public endpoint. Save a message to a conversation. Triggers lead capture if email or phone is detected in the message.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
        { field: "sender", type: "string", desc: "user or bot", req: true },
        { field: "text", type: "string", desc: "Message content", req: true },
        {
          field: "questionId",
          type: "string",
          desc: "If replying to a specific question",
          req: false,
        },
        { field: "intent", type: "string", desc: "Message intent", req: false },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Always true", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/conversation/message" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID", "sender": "user", "text": "I need help with my order"}\'',
    },
    {
      method: "POST",
      path: "/api/conversation/ai-message",
      auth: "none",
      desc: "Public endpoint. Send a message to the AI chatbot. Checks plan limits, retrieves RAG context from knowledge base, and returns AI-generated reply.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
        {
          field: "text",
          type: "string",
          desc: "User's message to the AI",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "reply",
          type: "string",
          desc: "AI-generated response text",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/conversation/ai-message" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID", "text": "What are your business hours?"}\'',
    },
    {
      method: "POST",
      path: "/api/conversation/request-live",
      auth: "none",
      desc: "Public endpoint. Request a live agent. Auto-assigns if exactly one agent is online, otherwise notifies the bot owner.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "autoAssigned",
          type: "boolean",
          desc: "Whether an agent was auto-assigned",
          req: true,
        },
        {
          field: "agentName",
          type: "string",
          desc: "Agent name if auto-assigned",
          req: false,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/conversation/request-live" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID"}\'',
    },
    {
      method: "POST",
      path: "/api/conversation/rate",
      auth: "none",
      desc: "Public endpoint. Submit a rating (1-5 stars) for a conversation.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
        { field: "rating", type: "number", desc: "Rating 1-5", req: true },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Always true", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/conversation/rate" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID", "rating": 5}\'',
    },
    {
      method: "POST",
      path: "/api/conversation/end",
      auth: "none",
      desc: "Public endpoint. End/close a conversation session.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Always true", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/conversation/end" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID"}\'',
    },
    {
      method: "GET",
      path: "/api/conversation/list",
      auth: "jwt",
      desc: "List conversations with optional filtering by bot. Supports pagination.",
      body: [
        {
          field: "botId",
          type: "string",
          desc: "Filter by bot ID (query param)",
          req: false,
        },
        {
          field: "page",
          type: "number",
          desc: "Page number (default 1, query param)",
          req: false,
        },
        {
          field: "limit",
          type: "number",
          desc: "Results per page (default 50, max 100, query param)",
          req: false,
        },
      ],
      response: [
        {
          field: "conversations",
          type: "array",
          desc: "Array of conversation objects",
          req: true,
        },
        {
          field: "total",
          type: "number",
          desc: "Total matching conversations",
          req: true,
        },
        { field: "page", type: "number", desc: "Current page", req: true },
        { field: "pages", type: "number", desc: "Total pages", req: true },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/conversation/list?page=1&limit=20" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/conversation/export-csv",
      auth: "jwt",
      desc: "Export all conversations as a CSV file download. Columns: Name, Status, Messages, Started, Last Activity.",
      curl: 'curl -X GET "https://api.talktokam.app/api/conversation/export-csv" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  --output conversations.csv',
    },
    {
      method: "GET",
      path: "/api/conversation/session/:chatbotId/:sessionId",
      auth: "none",
      desc: "Public endpoint. Get messages for a specific session. Used by the widget for polling new messages.",
      curl: 'curl -X GET "https://api.talktokam.app/api/conversation/session/BOT_CHATBOT_ID/SESSION_ID"',
    },
    {
      method: "PATCH",
      path: "/api/conversation/:id/status",
      auth: "jwt",
      desc: "Update conversation status. Requires 'live_chat' plan feature. Emits socket events and triggers webhooks.",
      body: [
        {
          field: "status",
          type: "string",
          desc: "active | closed | live_requested",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        { field: "status", type: "string", desc: "Updated status", req: true },
      ],
      plan: "live_chat",
      curl: 'curl -X PATCH "https://api.talktokam.app/api/conversation/CONV_ID/status" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"status": "closed"}\'',
    },
  ],
  "ai-agents": [
    {
      method: "GET",
      path: "/api/ai-agents",
      auth: "jwt",
      desc: "List all AI agents for the authenticated user.",
      curl: 'curl -X GET "https://api.talktokam.app/api/ai-agents" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/ai-agents",
      auth: "jwt",
      desc: "Create a new AI agent. Choose from 5 templates: Sales, Support, Lead Qualifier, Appointment, FAQ.",
      body: [
        {
          field: "name",
          type: "string",
          desc: "Agent display name",
          req: true,
        },
        {
          field: "template",
          type: "string",
          desc: "Template key (sales, support, lead, appointment, faq)",
          req: true,
        },
        {
          field: "config",
          type: "object",
          desc: "Agent configuration (greeting, systemPrompt, suggestedQuestions)",
          req: false,
        },
        {
          field: "training",
          type: "object",
          desc: "Training data config",
          req: false,
        },
      ],
      response: [
        {
          field: "—",
          type: "object",
          desc: "Created AI agent object",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/ai-agents" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"name": "Sales Bot", "template": "sales"}\'',
    },
    {
      method: "GET",
      path: "/api/ai-agents/:id",
      auth: "jwt",
      desc: "Get a single AI agent by ID. Ownership check applies.",
      curl: 'curl -X GET "https://api.talktokam.app/api/ai-agents/AGENT_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "PUT",
      path: "/api/ai-agents/:id",
      auth: "jwt",
      desc: "Update an AI agent's settings. Supports partial updates.",
      body: [
        { field: "name", type: "string", desc: "Agent name", req: false },
        {
          field: "config",
          type: "object",
          desc: "Updated configuration",
          req: false,
        },
        {
          field: "training",
          type: "object",
          desc: "Updated training config",
          req: false,
        },
      ],
      curl: 'curl -X PUT "https://api.talktokam.app/api/ai-agents/AGENT_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"config": {"greeting": "Hi! How can I help?"}}\'',
    },
    {
      method: "DELETE",
      path: "/api/ai-agents/:id",
      auth: "jwt",
      desc: "Permanently delete an AI agent.",
      response: [
        {
          field: "ok",
          type: "boolean",
          desc: "Always true on success",
          req: true,
        },
      ],
      curl: 'curl -X DELETE "https://api.talktokam.app/api/ai-agents/AGENT_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/ai-agents/:id/chat",
      auth: "none",
      desc: "Public endpoint. Send a message to an AI agent. Uses Groq API with Llama 3.1 8B. Handles conversation end, human takeover, and lead capture.",
      body: [
        { field: "message", type: "string", desc: "User's message", req: true },
        {
          field: "sessionId",
          type: "string",
          desc: "Unique session identifier",
          req: true,
        },
        {
          field: "history",
          type: "array",
          desc: "Previous messages for context",
          req: false,
        },
        {
          field: "visitorName",
          type: "string",
          desc: "Visitor's name",
          req: false,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "reply",
          type: "string",
          desc: "AI agent's response",
          req: true,
        },
        {
          field: "humanTriggered",
          type: "boolean",
          desc: "Agent requested human handoff",
          req: false,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/ai-agents/AGENT_ID/chat" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"message": "I need help with billing", "sessionId": "SESSION_ID"}\'',
    },
    {
      method: "GET",
      path: "/api/ai-agents/:id/info",
      auth: "none",
      desc: "Public endpoint. Get AI agent's public info — name, greeting, avatar, theme color, and suggested questions.",
      curl: 'curl -X GET "https://api.talktokam.app/api/ai-agents/AGENT_ID/info"',
    },
    {
      method: "GET",
      path: "/api/ai-agents/:id/messages/:sessionId",
      auth: "none",
      desc: "Public endpoint. Poll for new AI agent messages in a session. Used by the widget.",
      curl: 'curl -X GET "https://api.talktokam.app/api/ai-agents/AGENT_ID/messages/SESSION_ID"',
    },
    {
      method: "POST",
      path: "/api/ai-agents/:id/request-human",
      auth: "none",
      desc: "Public endpoint. Visitor requests to speak with a human agent. Saves lead info, emits webhook, and emails the bot owner.",
      body: [
        {
          field: "history",
          type: "array",
          desc: "Conversation history for context",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/ai-agents/AGENT_ID/request-human" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"sessionId": "SESSION_ID", "history": []}\'',
    },
    {
      method: "GET",
      path: "/api/ai-agents/:id/leads",
      auth: "jwt",
      desc: "Get leads captured by an AI agent. Includes visitor name, email, phone, and message history.",
      curl: 'curl -X GET "https://api.talktokam.app/api/ai-agents/AGENT_ID/leads" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/ai-agents/:id/conversations",
      auth: "jwt",
      desc: "Get all conversations for an AI agent.",
      curl: 'curl -X GET "https://api.talktokam.app/api/ai-agents/AGENT_ID/conversations" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/ai-agents/:id/conversations/:convoId/reply",
      auth: "jwt",
      desc: "Owner replies directly to a visitor in an AI agent conversation. The visitor sees the reply as if from the agent.",
      body: [
        {
          field: "text",
          type: "string",
          desc: "Owner's reply message",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Always true", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/ai-agents/AGENT_ID/conversations/CONVO_ID/reply" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"text": "Thanks for your patience! Let me help you."}\'',
    },
  ],
  knowledge: [
    {
      method: "POST",
      path: "/api/bots/:id/knowledge/upload",
      auth: "jwt",
      desc: "Upload a PDF file as a knowledge base for a bot. The system extracts text, chunks it, creates embeddings, and stores them for RAG retrieval. Max file size: 10MB.",
      body: [
        {
          field: "file",
          type: "file",
          desc: "PDF file (multipart/form-data)",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "message",
          type: "string",
          desc: "Upload result message",
          req: true,
        },
        {
          field: "chunks",
          type: "number",
          desc: "Number of text chunks created",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/bots/BOT_ID/knowledge/upload" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -F "file=@document.pdf"',
    },
    {
      method: "GET",
      path: "/api/bots/:id/knowledge",
      auth: "jwt",
      desc: "List all uploaded knowledge base documents for a bot.",
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "documents",
          type: "array",
          desc: "[{ name, chunks }]",
          req: true,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/BOT_ID/knowledge" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/bots/:id/knowledge/:docName",
      auth: "jwt",
      desc: "Get all text chunks for a specific uploaded document.",
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "chunks",
          type: "array",
          desc: "[{ chunkIndex, text }]",
          req: true,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/BOT_ID/knowledge/DOC_NAME" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "DELETE",
      path: "/api/bots/:id/knowledge/:docName",
      auth: "jwt",
      desc: "Delete an entire document and all its chunks from the knowledge base.",
      curl: 'curl -X DELETE "https://api.talktokam.app/api/bots/BOT_ID/knowledge/DOC_NAME" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "DELETE",
      path: "/api/bots/:id/knowledge/chunk/:chunkId",
      auth: "jwt",
      desc: "Delete a single knowledge chunk by its ID.",
      curl: 'curl -X DELETE "https://api.talktokam.app/api/bots/BOT_ID/knowledge/chunk/CHUNK_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  crawl: [
    {
      method: "POST",
      path: "/api/bots/:id/crawl/start",
      auth: "jwt",
      desc: "Start crawling a website to use as knowledge base. The URL must match a whitelisted domain.",
      body: [
        {
          field: "url",
          type: "string",
          desc: "Website URL (must start with http:// or https://)",
          req: true,
        },
        {
          field: "maxPages",
          type: "number",
          desc: "Max pages to crawl (default 50)",
          req: false,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "queuePosition",
          type: "number",
          desc: "Position in crawl queue",
          req: true,
        },
        {
          field: "totalAhead",
          type: "number",
          desc: "Jobs ahead in queue",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/bots/BOT_ID/crawl/start" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"url": "https://example.com", "maxPages": 30}\'',
    },
    {
      method: "GET",
      path: "/api/bots/:id/crawl/status",
      auth: "jwt",
      desc: "Get the current status of a crawl session for a bot.",
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/BOT_ID/crawl/status" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/bots/:id/crawl/pages",
      auth: "jwt",
      desc: "Get the list of pages that have been crawled for a bot.",
      curl: 'curl -X GET "https://api.talktokam.app/api/bots/BOT_ID/crawl/pages" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/bots/:id/crawl/stop",
      auth: "jwt",
      desc: "Stop an active or queued crawl job.",
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "message",
          type: "string",
          desc: "Confirmation message",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/bots/BOT_ID/crawl/stop" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  analytics: [
    {
      method: "GET",
      path: "/api/analytics/overview",
      auth: "jwt",
      desc: "Get analytics overview for your bots. Includes conversation counts, message volume, status breakdown, daily trends, top users, and average rating. Requires 'analytics' plan feature.",
      body: [
        {
          field: "botId",
          type: "string",
          desc: "Filter by specific bot (query param)",
          req: false,
        },
        {
          field: "days",
          type: "number",
          desc: "Lookback period in days (default 7, query param)",
          req: false,
        },
      ],
      response: [
        {
          field: "totalConversations",
          type: "number",
          desc: "Total conversation count",
          req: true,
        },
        {
          field: "totalMessages",
          type: "number",
          desc: "Total message count",
          req: true,
        },
        {
          field: "statusBreakdown",
          type: "object",
          desc: "{ active, closed, live_requested }",
          req: true,
        },
        {
          field: "dailyConversations",
          type: "object",
          desc: "{ labels: [...], values: [...] }",
          req: true,
        },
        {
          field: "topUsers",
          type: "array",
          desc: "Most active visitors",
          req: true,
        },
        {
          field: "avgRating",
          type: "number",
          desc: "Average star rating",
          req: true,
        },
      ],
      plan: "analytics",
      curl: 'curl -X GET "https://api.talktokam.app/api/analytics/overview?days=30" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  "live-chat": [
    {
      method: "GET",
      path: "/api/live-chat/stats",
      auth: "jwt",
      desc: "Get live agent session stats — resolved today, resolved total, and currently active chats.",
      plan: "live_chat",
      curl: 'curl -X GET "https://api.talktokam.app/api/live-chat/stats" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/live-chat/queue",
      auth: "jwt",
      desc: "Get pending live chat requests for the agent's owner's bots.",
      plan: "live_chat",
      curl: 'curl -X GET "https://api.talktokam.app/api/live-chat/queue" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/live-chat/active",
      auth: "jwt",
      desc: "Get active live chats currently assigned to this agent.",
      plan: "live_chat",
      curl: 'curl -X GET "https://api.talktokam.app/api/live-chat/active" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/live-chat/accept",
      auth: "jwt",
      desc: "Accept a queued chat request. Assigns the conversation to the accepting agent.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
      ],
      plan: "live_chat",
      curl: 'curl -X POST "https://api.talktokam.app/api/live-chat/accept" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID"}\'',
    },
    {
      method: "POST",
      path: "/api/live-chat/message",
      auth: "jwt",
      desc: "Agent sends a message in a live chat session.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
        { field: "text", type: "string", desc: "Message content", req: true },
      ],
      plan: "live_chat",
      curl: 'curl -X POST "https://api.talktokam.app/api/live-chat/message" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID", "text": "Hi! How can I help you today?"}\'',
    },
    {
      method: "POST",
      path: "/api/live-chat/resolve",
      auth: "jwt",
      desc: "Resolve/close a live chat conversation.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
      ],
      plan: "live_chat",
      curl: 'curl -X POST "https://api.talktokam.app/api/live-chat/resolve" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID"}\'',
    },
    {
      method: "POST",
      path: "/api/live-chat/transfer",
      auth: "jwt",
      desc: "Transfer a live chat to another agent.",
      body: [
        {
          field: "chatbotId",
          type: "string",
          desc: "Bot's chatbot ID",
          req: true,
        },
        {
          field: "sessionId",
          type: "string",
          desc: "Session identifier",
          req: true,
        },
        {
          field: "toAgentId",
          type: "string",
          desc: "Target agent's user ID",
          req: true,
        },
      ],
      plan: "live_chat",
      curl: 'curl -X POST "https://api.talktokam.app/api/live-chat/transfer" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"chatbotId": "BOT_CHATBOT_ID", "sessionId": "SESSION_ID", "toAgentId": "AGENT_USER_ID"}\'',
    },
    {
      method: "GET",
      path: "/api/live-chat/agents",
      auth: "jwt",
      desc: "Get list of all agents and supervisors for the bot owner.",
      plan: "live_chat",
      curl: 'curl -X GET "https://api.talktokam.app/api/live-chat/agents" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  agents: [
    {
      method: "GET",
      path: "/api/agents",
      auth: "jwt",
      desc: "List all sub-agents created by the authenticated user.",
      curl: 'curl -X GET "https://api.talktokam.app/api/agents" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/agents",
      auth: "jwt",
      desc: "Add a new sub-agent (agent or supervisor). Respects plan's liveAgents limit.",
      body: [
        {
          field: "email",
          type: "string",
          desc: "Agent's email address",
          req: true,
        },
        {
          field: "password",
          type: "string",
          desc: "Agent's login password",
          req: true,
        },
        {
          field: "fullName",
          type: "string",
          desc: "Agent's full name",
          req: true,
        },
        {
          field: "role",
          type: "string",
          desc: "agent or supervisor (default: agent)",
          req: false,
        },
      ],
      response: [
        { field: "_id", type: "string", desc: "Created agent's ID", req: true },
        { field: "email", type: "string", desc: "Agent email", req: true },
        { field: "fullName", type: "string", desc: "Agent name", req: true },
        { field: "role", type: "string", desc: "Assigned role", req: true },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/agents" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"email": "agent@example.com", "password": "AgentP@ss1", "fullName": "Jane Agent", "role": "agent"}\'',
    },
    {
      method: "DELETE",
      path: "/api/agents/:id",
      auth: "jwt",
      desc: "Remove a sub-agent. Only the owner who created them can delete.",
      curl: 'curl -X DELETE "https://api.talktokam.app/api/agents/AGENT_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "PATCH",
      path: "/api/agents/status",
      auth: "jwt",
      desc: "Agent updates their own online/offline status. Going offline moves active chats back to the queue.",
      body: [
        {
          field: "status",
          type: "string",
          desc: "online or offline",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        { field: "status", type: "string", desc: "Updated status", req: true },
      ],
      curl: 'curl -X PATCH "https://api.talktokam.app/api/agents/status" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"status": "online"}\'',
    },
  ],
  webhooks: [
    {
      method: "GET",
      path: "/api/webhooks",
      auth: "jwt",
      desc: "List all configured webhooks for the authenticated user.",
      curl: 'curl -X GET "https://api.talktokam.app/api/webhooks" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/webhooks",
      auth: "jwt",
      desc: "Create a new webhook endpoint. The system will POST event data to your URL whenever the specified events occur.",
      body: [
        {
          field: "name",
          type: "string",
          desc: "Webhook name (default: 'My Webhook')",
          req: false,
        },
        { field: "url", type: "string", desc: "Your endpoint URL", req: true },
        {
          field: "events",
          type: "array",
          desc: "Events to listen for: new_conversation, new_lead, chat_resolved, etc.",
          req: true,
        },
      ],
      response: [
        {
          field: "—",
          type: "object",
          desc: "Created webhook object with auto-generated secret",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/webhooks" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"name": "My Webhook", "url": "https://myapp.com/webhook", "events": ["new_lead", "chat_resolved"]}\'',
    },
    {
      method: "PUT",
      path: "/api/webhooks/:id",
      auth: "jwt",
      desc: "Update a webhook's name, URL, events, or active status.",
      body: [
        { field: "name", type: "string", desc: "Updated name", req: false },
        {
          field: "url",
          type: "string",
          desc: "Updated endpoint URL",
          req: false,
        },
        {
          field: "events",
          type: "array",
          desc: "Updated event list",
          req: false,
        },
        {
          field: "isActive",
          type: "boolean",
          desc: "Enable/disable webhook",
          req: false,
        },
      ],
      curl: 'curl -X PUT "https://api.talktokam.app/api/webhooks/WEBHOOK_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"isActive": false}\'',
    },
    {
      method: "DELETE",
      path: "/api/webhooks/:id",
      auth: "jwt",
      desc: "Delete a webhook and all its associated logs.",
      response: [
        { field: "ok", type: "boolean", desc: "Always true", req: true },
      ],
      curl: 'curl -X DELETE "https://api.talktokam.app/api/webhooks/WEBHOOK_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/webhooks/:id/test",
      auth: "jwt",
      desc: "Send a test payload to your webhook URL. Useful for verifying your endpoint is working correctly.",
      response: [
        {
          field: "ok",
          type: "boolean",
          desc: "Whether the request succeeded",
          req: true,
        },
        {
          field: "status",
          type: "number",
          desc: "HTTP status from your endpoint",
          req: false,
        },
        {
          field: "body",
          type: "string",
          desc: "Response body from your endpoint",
          req: false,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/webhooks/WEBHOOK_ID/test" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/webhooks/logs",
      auth: "jwt",
      desc: "Get webhook delivery logs with filtering. Supports pagination.",
      body: [
        {
          field: "webhookId",
          type: "string",
          desc: "Filter by webhook (query param)",
          req: false,
        },
        {
          field: "event",
          type: "string",
          desc: "Filter by event type (query param)",
          req: false,
        },
        {
          field: "status",
          type: "string",
          desc: "Filter by status (query param)",
          req: false,
        },
        {
          field: "page",
          type: "number",
          desc: "Page number (query param)",
          req: false,
        },
        {
          field: "limit",
          type: "number",
          desc: "Results per page, max 100 (query param)",
          req: false,
        },
      ],
      response: [
        {
          field: "logs",
          type: "array",
          desc: "Array of webhook log entries",
          req: true,
        },
        {
          field: "total",
          type: "number",
          desc: "Total matching logs",
          req: true,
        },
        { field: "page", type: "number", desc: "Current page", req: true },
        { field: "pages", type: "number", desc: "Total pages", req: true },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/webhooks/logs?page=1&limit=20" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/webhooks/logs/stats",
      auth: "jwt",
      desc: "Get aggregated webhook log statistics grouped by event type.",
      response: [
        {
          field: "—",
          type: "array",
          desc: "[{ _id: event_name, total, success, failed }]",
          req: true,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/webhooks/logs/stats" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  canned: [
    {
      method: "GET",
      path: "/api/canned",
      auth: "jwt",
      desc: "List all canned (pre-written) responses for the authenticated user. Sorted by shortcut.",
      curl: 'curl -X GET "https://api.talktokam.app/api/canned" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/canned",
      auth: "jwt",
      desc: "Create a new canned response with a shortcut and message text.",
      body: [
        {
          field: "shortcut",
          type: "string",
          desc: "Slash command shortcut e.g. /greeting",
          req: true,
        },
        {
          field: "text",
          type: "string",
          desc: "The canned response message",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/canned" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"shortcut": "/greeting", "text": "Hi! Welcome to our support. How can I help you today?"}\'',
    },
    {
      method: "DELETE",
      path: "/api/canned/:id",
      auth: "jwt",
      desc: "Delete a canned response by ID. Ownership check applies.",
      response: [
        { field: "ok", type: "boolean", desc: "Always true", req: true },
      ],
      curl: 'curl -X DELETE "https://api.talktokam.app/api/canned/CANNED_ID" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  orders: [
    {
      method: "POST",
      path: "/api/orders",
      auth: "none",
      desc: "Public endpoint. Create a new order from the chatbot widget. Used for e-commerce flows.",
      body: [
        { field: "botId", type: "string", desc: "Bot ID", req: true },
        {
          field: "sessionId",
          type: "string",
          desc: "Widget session ID",
          req: false,
        },
        {
          field: "visitorName",
          type: "string",
          desc: "Customer name",
          req: false,
        },
        {
          field: "visitorEmail",
          type: "string",
          desc: "Customer email",
          req: false,
        },
        {
          field: "items",
          type: "array",
          desc: "[{ name, price, qty }]",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/orders" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"botId": "BOT_ID", "sessionId": "SESSION_ID", "items": [{"name": "Product A", "price": 29.99, "qty": 2}]}\'',
    },
    {
      method: "GET",
      path: "/api/orders",
      auth: "jwt",
      desc: "List orders for the authenticated user's bots. Supports pagination and status filtering.",
      body: [
        {
          field: "page",
          type: "number",
          desc: "Page number (query param)",
          req: false,
        },
        {
          field: "limit",
          type: "number",
          desc: "Results per page, max 100 (query param)",
          req: false,
        },
        {
          field: "status",
          type: "string",
          desc: "Filter by status (query param)",
          req: false,
        },
      ],
      response: [
        {
          field: "orders",
          type: "array",
          desc: "Array of order objects",
          req: true,
        },
        { field: "total", type: "number", desc: "Total orders", req: true },
        { field: "page", type: "number", desc: "Current page", req: true },
        { field: "pages", type: "number", desc: "Total pages", req: true },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/orders?page=1&limit=20" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "PATCH",
      path: "/api/orders/:id/status",
      auth: "jwt",
      desc: "Update an order's status: pending, confirmed, or cancelled.",
      body: [
        {
          field: "status",
          type: "string",
          desc: "pending | confirmed | cancelled",
          req: true,
        },
      ],
      curl: 'curl -X PATCH "https://api.talktokam.app/api/orders/ORDER_ID/status" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"status": "confirmed"}\'',
    },
  ],
  plans: [
    {
      method: "GET",
      path: "/api/plans",
      auth: "jwt",
      desc: "List all available subscription plans. Plans are auto-seeded if none exist.",
      curl: 'curl -X GET "https://api.talktokam.app/api/plans" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/plans/:slug",
      auth: "jwt",
      desc: "Get a single plan by its slug (e.g., 'free', 'starter', 'growth', 'enterprise').",
      curl: 'curl -X GET "https://api.talktokam.app/api/plans/free" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/plans/assign",
      auth: "jwt",
      desc: "Assign a plan to the authenticated user. Updates plan limits immediately.",
      body: [
        {
          field: "planId",
          type: "string",
          desc: "Plan ID to assign",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/plans/assign" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"planId": "PLAN_ID"}\'',
    },
  ],
  upload: [
    {
      method: "POST",
      path: "/api/upload",
      auth: "none",
      desc: "Public endpoint. Upload a file (image, PDF, or document) to Cloudinary. Used by the widget for file upload questions.",
      body: [
        {
          field: "file",
          type: "file",
          desc: "File (multipart/form-data). Allowed: jpg, jpeg, png, gif, pdf, doc, docx, webp",
          req: true,
        },
      ],
      response: [
        { field: "ok", type: "boolean", desc: "Success indicator", req: true },
        {
          field: "url",
          type: "string",
          desc: "Public Cloudinary URL of uploaded file",
          req: true,
        },
        {
          field: "filename",
          type: "string",
          desc: "Original filename",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/upload" \\\n  -F "file=@image.jpg"',
    },
  ],
  "2fa": [
    {
      method: "GET",
      path: "/api/2fa/status",
      auth: "jwt",
      desc: "Check whether two-factor authentication is enabled for your account.",
      plan: "2fa",
      response: [
        {
          field: "enabled",
          type: "boolean",
          desc: "Whether 2FA is active",
          req: true,
        },
      ],
      curl: 'curl -X GET "https://api.talktokam.app/api/2fa/status" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/2fa/generate",
      auth: "jwt",
      desc: "Generate a new 2FA secret and QR code URL. Scan the QR with Google Authenticator or Authy.",
      plan: "2fa",
      response: [
        { field: "secret", type: "string", desc: "TOTP secret key", req: true },
        {
          field: "qrUrl",
          type: "string",
          desc: "URL to generate QR code",
          req: true,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/2fa/generate" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/2fa/verify",
      auth: "jwt",
      desc: "Verify a TOTP token from your authenticator app and enable 2FA.",
      body: [
        {
          field: "token",
          type: "string",
          desc: "6-digit code from authenticator app",
          req: true,
        },
      ],
      response: [
        {
          field: "enabled",
          type: "boolean",
          desc: "true on success",
          req: true,
        },
        {
          field: "message",
          type: "string",
          desc: "Confirmation message",
          req: true,
        },
      ],
      plan: "2fa",
      curl: 'curl -X POST "https://api.talktokam.app/api/2fa/verify" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"token": "123456"}\'',
    },
    {
      method: "POST",
      path: "/api/2fa/disable",
      auth: "jwt",
      desc: "Disable two-factor authentication on your account.",
      plan: "2fa",
      curl: 'curl -X POST "https://api.talktokam.app/api/2fa/disable" \\\n  -H "Authorization: Bearer YOUR_TOKEN"',
    },
  ],
  admin: [
    {
      method: "GET",
      path: "/api/admin/overview",
      auth: "admin",
      desc: "Get admin dashboard overview with platform-wide stats: total users, bots, conversations, plans, and recent users.",
      curl: 'curl -X GET "https://api.talktokam.app/api/admin/overview" \\\n  -H "Authorization: Bearer ADMIN_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/admin/users",
      auth: "admin",
      desc: "List all users with pagination and search. Query params: page, limit, search (matches name or email).",
      curl: 'curl -X GET "https://api.talktokam.app/api/admin/users?page=1&limit=50&search=john" \\\n  -H "Authorization: Bearer ADMIN_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/admin/users/:id",
      auth: "admin",
      desc: "Get detailed user info including their bots and conversation count.",
      curl: 'curl -X GET "https://api.talktokam.app/api/admin/users/USER_ID" \\\n  -H "Authorization: Bearer ADMIN_TOKEN"',
    },
    {
      method: "PUT",
      path: "/api/admin/users/:id/plan",
      auth: "admin",
      desc: "Assign or change a user's subscription plan.",
      body: [
        { field: "planId", type: "string", desc: "Target plan ID", req: true },
      ],
      curl: 'curl -X PUT "https://api.talktokam.app/api/admin/users/USER_ID/plan" \\\n  -H "Authorization: Bearer ADMIN_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"planId": "PLAN_ID"}\'',
    },
    {
      method: "DELETE",
      path: "/api/admin/users/:id",
      auth: "admin",
      desc: "Delete a user and all their associated bots from the platform.",
      curl: 'curl -X DELETE "https://api.talktokam.app/api/admin/users/USER_ID" \\\n  -H "Authorization: Bearer ADMIN_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/admin/bots",
      auth: "admin",
      desc: "List all bots across the platform with pagination and search.",
      curl: 'curl -X GET "https://api.talktokam.app/api/admin/bots?page=1&limit=50" \\\n  -H "Authorization: Bearer ADMIN_TOKEN"',
    },
    {
      method: "GET",
      path: "/api/admin/conversations",
      auth: "admin",
      desc: "List all conversations across the platform with pagination.",
      curl: 'curl -X GET "https://api.talktokam.app/api/admin/conversations?page=1&limit=50" \\\n  -H "Authorization: Bearer ADMIN_TOKEN"',
    },
    {
      method: "POST",
      path: "/api/admin/plans",
      auth: "admin",
      desc: "Create a new subscription plan.",
      curl: 'curl -X POST "https://api.talktokam.app/api/admin/plans" \\\n  -H "Authorization: Bearer ADMIN_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"name": "Custom Plan", "slug": "custom", "price": 99}\'',
    },
    {
      method: "PUT",
      path: "/api/admin/plans/:id",
      auth: "admin",
      desc: "Update an existing plan's configuration.",
      curl: 'curl -X PUT "https://api.talktokam.app/api/admin/plans/PLAN_ID" \\\n  -H "Authorization: Bearer ADMIN_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"price": 149}\'',
    },
    {
      method: "DELETE",
      path: "/api/admin/plans/:id",
      auth: "admin",
      desc: "Delete a plan from the platform.",
      curl: 'curl -X DELETE "https://api.talktokam.app/api/admin/plans/PLAN_ID" \\\n  -H "Authorization: Bearer ADMIN_TOKEN"',
    },
  ],
  proxy: [
    {
      method: "POST",
      path: "/api/proxy/fetch",
      auth: "jwt",
      desc: "Proxy HTTP requests to external URLs. Blocks private/internal IPs for security. Has configurable timeout (default 10s, max 30s) and 5MB response limit.",
      body: [
        {
          field: "url",
          type: "string",
          desc: "Target URL to fetch",
          req: true,
        },
        {
          field: "method",
          type: "string",
          desc: "GET, POST, PUT, DELETE, or PATCH (default GET)",
          req: false,
        },
        {
          field: "headers",
          type: "object",
          desc: "Custom HTTP headers",
          req: false,
        },
        {
          field: "body",
          type: "string|object",
          desc: "Request body (for POST/PUT/PATCH)",
          req: false,
        },
        {
          field: "timeoutMs",
          type: "number",
          desc: "Timeout in milliseconds (max 30000)",
          req: false,
        },
      ],
      response: [
        {
          field: "ok",
          type: "boolean",
          desc: "Whether the request succeeded",
          req: true,
        },
        {
          field: "status",
          type: "number",
          desc: "HTTP status from target",
          req: false,
        },
        {
          field: "data",
          type: "object|string",
          desc: "Response data from target",
          req: false,
        },
      ],
      curl: 'curl -X POST "https://api.talktokam.app/api/proxy/fetch" \\\n  -H "Authorization: Bearer YOUR_TOKEN" \\\n  -H "Content-Type: application/json" \\\n  -d \'{"url": "https://api.example.com/data", "method": "GET"}\'',
    },
  ],
};

const ApiDocs = () => {
  const [search, setSearch] = useState("");

  const filtered = TOC_SECTIONS.map((s) => ({
    ...s,
    endpoints: (ENDPOINTS[s.id] || []).filter(
      (ep) =>
        ep.path.toLowerCase().includes(search.toLowerCase()) ||
        ep.desc.toLowerCase().includes(search.toLowerCase()),
    ),
  })).filter((s) => s.endpoints.length > 0);

  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 8, md: 12 },
          overflow: "hidden",
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
              }}
            >
              Developer-First API
            </Typography>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#94a3b8",
                mb: 3,
                maxWidth: 600,
                mx: "auto",
              }}
            >
              Build custom integrations with TalkToKam's REST API. Simple
              authentication, predictable responses, comprehensive coverage.
            </Typography>
            <TextField
              fullWidth
              placeholder="Search endpoints by path or description..."
              size="medium"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} color="#64748b" />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: "12px",
                  bgcolor: "rgba(255,255,255,0.06)",
                  "& fieldset": { border: "1px solid rgba(255,255,255,0.1)" },
                  "& input": { color: "#cbd5e1", fontSize: "0.95rem" },
                  "&:hover fieldset": { borderColor: `${BRAND.cyan}44` },
                  "&.Mui-focused fieldset": { borderColor: BRAND.cyan },
                },
              }}
            />
          </motion.div>
        </Container>
      </Box>

      <Container
        maxWidth="lg"
        sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 6, md: 10 } }}
      >
        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Box
              sx={{
                position: "sticky",
                top: 90,
                display: { xs: "none", md: "block" },
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  color: "#64748b",
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mb: 2,
                }}
              >
                API Reference
              </Typography>
              {TOC_SECTIONS.map((s) => (
                <Box
                  key={s.id}
                  component="a"
                  href={`#${s.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    py: 0.6,
                    px: 1.5,
                    borderRadius: 1,
                    textDecoration: "none",
                    color: "#94a3b8",
                    fontSize: "0.8rem",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.04)",
                      color: BRAND.cyan,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexShrink: 0,
                    }}
                  >
                    <s.icon size={16} color="#94a3b8" />
                  </Box>
                  <Typography sx={{ fontSize: "0.8rem" }}>{s.title}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={9}>
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                gap: 0.5,
                mb: 3,
                overflowX: "auto",
                pb: 1,
              }}
            >
              {TOC_SECTIONS.map((s) => (
                <Box
                  key={s.id}
                  component="a"
                  href={`#${s.id}`}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    textDecoration: "none",
                    px: 1.5,
                    py: 0.6,
                    borderRadius: 2,
                    bgcolor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.06)",
                    whiteSpace: "nowrap",
                    "&:hover": { borderColor: BRAND.cyan },
                  }}
                >
                  <s.icon size={14} color="#94a3b8" />
                  <Typography sx={{ fontSize: "0.75rem", color: "#94a3b8" }}>
                    {s.title}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                mb: 3,
                p: 2,
                borderRadius: 3,
                bgcolor: "rgba(99,102,241,0.06)",
                border: "1px solid rgba(99,102,241,0.2)",
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.85rem",
                  color: "#c7d2fe",
                  lineHeight: 1.6,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Lock size={18} color="#818cf8" />
                <span>
                  <strong>Authentication:</strong> All protected endpoints
                  require a{" "}
                  <code style={{ color: "#38bdf8" }}>
                    Authorization: Bearer YOUR_TOKEN
                  </code>{" "}
                  header. Generate your token from the TalkToKam dashboard under
                  Settings &gt; API.
                </span>
              </Typography>
            </Box>

            {filtered.map((group) => (
              <SectionCard
                key={group.id}
                id={group.id}
                icon={group.icon}
                title={group.title}
              >
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    mb: 2,
                    lineHeight: 1.6,
                  }}
                >
                  {group.endpoints.length} endpoint
                  {group.endpoints.length !== 1 ? "s" : ""}
                </Typography>
                {group.endpoints.map((ep, i) => (
                  <EndpointAccordion key={i} ep={ep} />
                ))}
              </SectionCard>
            ))}
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default ApiDocs;
